import { FC, useContext, useRef } from "react";
import { ReportCustomizationContext } from "../ReportCustomizationContext";
import { AdequacyAggregateGranularity, AdequacyAggregateType } from "@app/exports/enumerations";
import ExportService from "@app/exports/exportService";
import { ExportAdequacyAggregatesModel } from "@app/exports/exportModels";
import { GenerateSection } from "../components/GenerateSection";
import { ColumnConfigurationSection } from "../components/ColumnConfigurationSection";
import { ServiceAreaSection } from "../components/ServiceAreaSection";
import { FilterType } from "@app/projects/enumerators";
import { SpecialtySection } from "../components/SpecialtySection";
import { handleToastSuccess, handleToastError, reportTypeToColumnsEndpointMap } from "../helpers/helpers";
import { ReportCustomizationReducerActions } from "../interfaces/reportCustomizationReducerActionsEnum";
import { ReportType } from "../interfaces/reportTypeEnum";

export const AllSpecialtiesReport: FC = () => {
  const rcContext = useContext(ReportCustomizationContext);
  const exportService = useRef(new ExportService()).current;

  const getExportModel = () => {
    let granularity = rcContext.routeState.filters.find((f) => f.filterType === FilterType.County || f.filterType === FilterType.State) ? AdequacyAggregateGranularity.County : AdequacyAggregateGranularity.State;
    if (granularity === AdequacyAggregateGranularity.County) {
      if (rcContext.routeState.selectedRegionSetIds?.length === 0)
        granularity = AdequacyAggregateGranularity.State;
    } else {
      if (rcContext.routeState.selectedRegionSetIds?.length === 0)
        granularity = AdequacyAggregateGranularity.ServiceArea;
    }

    const selectedRegionSetId = rcContext.state.selectedCounties.map((c) => parseInt(c));
    const { primaryId, secondaryId } = rcContext.getSelectedRunConfigurationIdsInChronologicalOrder();

    const allSpecialtiesRequestModel: ExportAdequacyAggregatesModel = {
      // if a state or county filter is applied, it's county granularity
      aggregateGranularity: granularity,
      aggregateType: AdequacyAggregateType.SpecialtyGroup,
      comparedProjectRunConfigurationId: secondaryId,
      customProjectId: parseInt(rcContext.routeParams.projectId),
      eMailOnSuccess: rcContext.state.shouldEmailLink,
      exportName: rcContext.state.filename,
      formatType: rcContext.state.fileFormat,
      includeActiveRegionSets: rcContext.state.includeActive,
      includeApprovedRegionSets: rcContext.state.includeApproved,
      includeFacilitySpecialties: rcContext.state.includeFacilitySpecialties,
      includePendingRegionSets: rcContext.state.includePending,
      includePhysicianSpecialties: rcContext.state.includePhysicianSpecialties,
      networkAssessmentAnalysisTypes: rcContext.state.networkAssessmentAnalysisTypes,
      projectRunConfigurationId: primaryId,
      projectURL: `${window.customUIAbsolutePath}/projects/${rcContext.routeParams.projectId}`,
      regionSetGapFilter: rcContext.state.serviceAreaGapsType,
      regionSetIds: selectedRegionSetId,
      reportCustomizationId: rcContext.state.selectedPreset?.id,
      runAsynchronous: true,
      runFrequency: rcContext.state.runFrequency,
      sharedUsers: rcContext.state.sharedUsers,
      specialtyGapFilter: rcContext.state.specialtyGapsType,
      specialtyGroupCodes: rcContext.state.selectedSpecialties.map((sp) => sp.key),
    };

    return allSpecialtiesRequestModel;
  }

  // event handlers
  const handleGenerateClick = () => {
    rcContext.dispatch({ payload: { isGenerating: true }, type: ReportCustomizationReducerActions.ChangeIsGenerating });

    exportService.postExportAdequacyAggregates(getExportModel())
      .then(() => handleToastSuccess(rcContext.state.filename),
        () => handleToastError(rcContext.state.filename)
      ).then(() => {
        rcContext.dispatch({ payload: { isGenerating: false }, type: ReportCustomizationReducerActions.ChangeIsGenerating });
      });
  }

  return (
    <>
      <div className="container ms-0">
        <ServiceAreaSection />

        <SpecialtySection />

        <ColumnConfigurationSection
          columnsEndpoint={reportTypeToColumnsEndpointMap.get(ReportType.AdequacyAggregate)}
          exportModel={getExportModel()}
          presets={rcContext.state.presets}
          selectedPreset={rcContext.state.selectedPreset}
          reportType={rcContext.getReportType()}
          onPresetChange={rcContext.changeSelectedPreset}
          onPresetsRefresh={rcContext.changeLoadedPresets}
        />
      </div>
      <GenerateSection
        settingsAreValid={rcContext.validateSettings()}
        onGenerate={handleGenerateClick}
      />
    </>
  );
}